import {
  IonCol,
  IonGrid,
  IonItemOptions,
  IonItemOption,
  IonIcon,
  IonItem,
  IonItemSliding,
  IonItemDivider,
  IonLabel,
  IonRow,
  IonSpinner,
} from '@ionic/react'
import React, { useState } from 'react'
import { chevronDown, chevronUp, trash } from 'ionicons/icons'
import { PhotoViewer } from '@capacitor-community/photoviewer'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { deletePulsaraAttachment, fetchPulsaraAttachmentImage } from '../../store/pulsara.actions'
import { getPulsaraDetailsAttachments } from '../../selectors/pulsara.selector'
import { IPulsaraAttachmentImage } from '../../store/pulsara.types'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import './PulsaraAttachments.component.css'

const PulsaraAttachment: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const pulsaraAttachments = useSelector(getPulsaraDetailsAttachments)

  const toggleExpanded = (attachment: IPulsaraAttachmentImage) => {
    const id = String(attachment.id)
    const isAlreadyExpanded = expandedIds.has(id)
    if (!isAlreadyExpanded && !attachment.blobData) {
      dispatch(fetchPulsaraAttachmentImage({ attachmentId: attachment.id, pulsaraId: attachment.pulsaraId }))
    }
    setExpandedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  const showPhotoViewer = (photoViewerProps: { src: string; alt: string }) => {
    const { src, alt } = photoViewerProps

    PhotoViewer.show({
      images: [
        {
          url: src || '',
          title: alt,
        },
      ],
      options: {
        share: false,
      },
    })
  }

  const renderReportImage = (attachment: IPulsaraAttachmentImage) => {
    const mime = attachment.mime || 'image/png'
    const src = `data:${mime};base64,${attachment.blobData}`

    return (
      <div className='pulsara-attachment-strip-image'>
        <img src={src} alt={attachment.name} onClick={() => showPhotoViewer({ src, alt: attachment.name })} />
      </div>
    )
  }

  const onAttachmentDeleteClick = (attachment: IPulsaraAttachmentImage) => {
    dispatch(
      showAlert({
        header: t('pulsara.deleteAttachment.header'),
        message: t('pulsara.deleteAttachment.message'),
        buttons: [
          { text: t('common.buttons.Cancel') },
          {
            text: t('common.buttons.Ok'),
            role: 'destructive',
            handler: () => dispatch(deletePulsaraAttachment(attachment.id)),
          },
        ],
      })
    )
  }

  if (!pulsaraAttachments?.length) return <></>

  return (
    <>
      <IonItemDivider color='light' className='ion-padding attachment-sub-heading'>
        <IonLabel className='grid-item-divider-label' color='medium'>
          {t('attachments.PulsaraAttachments')}
        </IonLabel>
      </IonItemDivider>
      {pulsaraAttachments.map((data: IPulsaraAttachmentImage) => {
        const id = String(data.id)
        const isExpanded = expandedIds.has(id)
        const hasImage = Boolean(data.blobData)
        const isLoadingPreview = !hasImage && data.isUnavailable === null
        const isPreviewUnavailable = !hasImage && data.isUnavailable === true

        return (
          <IonItemSliding key={data.id}>
            <IonItem className='event-item'>
              <div className='event-item-main'>
                <IonGrid className='report-attachment-grid'>
                  <IonRow>
                    <IonCol className='report-attachment-title'>{data.name}</IonCol>
                    <IonCol className='data-point event-more' onClick={() => toggleExpanded(data)}>
                      <IonIcon color='medium' className='event-more-icon' icon={isExpanded ? chevronUp : chevronDown} />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </div>

              {isExpanded && (
                <div className='pulsara-attachment-strip'>
                  {hasImage && renderReportImage(data)}
                  {isLoadingPreview && <IonSpinner />}
                  {isPreviewUnavailable && (
                    <div className='pulsara-attachment-strip-no-image'>{t('pulsara.PreviewUnavailable')}</div>
                  )}
                </div>
              )}
            </IonItem>
            <IonItemOptions>
              <IonItemOption color='danger' onClick={() => onAttachmentDeleteClick(data)}>
                <IonIcon slot='start' icon={trash} />
                {t('common.buttons.Delete')}
              </IonItemOption>
            </IonItemOptions>
          </IonItemSliding>
        )
      })}
    </>
  )
}

export default PulsaraAttachment
